// RoomSettings — right-panel editor for a room's metadata.
//
// Displays General (avatar, name, topic, channel type), each saving
// itself with the shared SaveStatus beside the heading, and a collapsible
// Members link that routes into `MemberList`. Power-level gating disables
// every control the caller can't actually write; saving runs the writes in
// sequence.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, LogOut, Upload, X } from "lucide-react";

import {
  ALL_SPACES_OPTION_ID,
  ChannelTypePills,
  ErrorLine,
  SpaceSelect,
  TextInput,
} from "~/components/CreateRoomDialog";
import { IntegrationsSection } from "~/components/IntegrationsSection";
import { Section } from "~/components/SettingsSection";
import { LeaveRoomDialog } from "~/components/LeaveRoomDialog";
import { SaveStatus, type SaveState } from "@nurion/ui/save-status";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { useAutoResizeTextarea } from "@nurion/ui/use-auto-resize-textarea";
import { MemberList } from "~/components/MemberList";
import { UserAvatar } from "~/components/UserSearch";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useRoomSettings } from "~/hooks/useRoomSettings";
import { DEFAULT_CHANNEL_TYPE, type ChannelType } from "~/lib/channel-type";
import { canSendStateEvent as matrixCanSendStateEvent, type RoomSummary } from "~/lib/matrix";
import { spaceDestinationOptions } from "~/lib/spaces";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

export interface RoomSettingsProps {
  room: RoomSummary;
  onClose: () => void;
}

type View = "general" | "members";

interface EditableFields {
  name: string;
  topic: string;
  channelType: ChannelType;
  spaceId: string;
}

export function RoomSettings({ room, onClose }: RoomSettingsProps) {
  const [view, setView] = useState<View>("general");
  const [leaveOpen, setLeaveOpen] = useState(false);
  return (
    <>
      <aside
        aria-label="Room settings"
        style={{
          width: "var(--chat-right-panel-default-width)",
          minWidth: "var(--chat-right-panel-min-width)",
          maxWidth: "var(--chat-right-panel-max-width)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid var(--chat-surface-border)",
          background: "var(--chat-surface-panel)",
        }}
      >
        <PanelHeader view={view} onBack={() => setView("general")} onClose={onClose} />
        {view === "general" && (
          <GeneralSection
            room={room}
            onOpenMembers={() => setView("members")}
            onOpenLeave={() => setLeaveOpen(true)}
          />
        )}
        {view === "members" && <MemberList roomId={room.id} />}
      </aside>
      {leaveOpen && <LeaveRoomDialog room={room} onClose={() => setLeaveOpen(false)} />}
    </>
  );
}

function PanelHeader({
  view,
  onBack,
  onClose,
}: {
  view: View;
  onBack: () => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: "var(--chat-dialog-header-height)",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      {view !== "general" && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to general settings"
          className="chat-button"
          style={{
            width: "var(--chat-dialog-close-size)",
            height: "var(--chat-dialog-close-size)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            borderRadius: "var(--chat-radius-sm)",
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={14} strokeWidth={ICON_STROKE} />
        </button>
      )}
      <h2
        style={{
          flex: 1,
          margin: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 500,
        }}
      >
        {view === "general" ? "Room settings" : "Members"}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close settings"
        className="chat-button"
        style={{
          width: "var(--chat-dialog-close-size)",
          height: "var(--chat-dialog-close-size)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          borderRadius: "var(--chat-radius-sm)",
          cursor: "pointer",
        }}
      >
        <X size={14} strokeWidth={ICON_STROKE} />
      </button>
    </div>
  );
}

function GeneralSection({
  room,
  onOpenMembers,
  onOpenLeave,
}: {
  room: RoomSummary;
  onOpenMembers: () => void;
  onOpenLeave: () => void;
}) {
  const settings = useRoomSettings(room.id);
  const { client, userId, activeOrgSpaceId, moveRoomToSpace } = useMatrix();
  const rooms = useMatrixRooms();
  const { push: pushToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const currentParentSpaces = useMemo(
    () =>
      rooms.filter(
        (candidate) => candidate.isSpace && (candidate.spaceChildRoomIds ?? []).includes(room.id),
      ),
    [rooms, room.id],
  );
  const currentSpaceId = currentParentSpaces[0]?.id ?? "";
  const spaceOptions = useMemo(
    () => [
      { id: ALL_SPACES_OPTION_ID, name: "All spaces" },
      ...spaceDestinationOptions(rooms, activeOrgSpaceId),
    ],
    [rooms, activeOrgSpaceId],
  );

  const [name, setName] = useState(room.name);
  const [topic, setTopic] = useState(room.topic ?? "");
  const [channelType, setChannelType] = useState<ChannelType>(
    room.channelType ?? DEFAULT_CHANNEL_TYPE,
  );
  const [spaceId, setSpaceId] = useState(currentSpaceId);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(room.avatarUrl);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  useEffect(() => {
    if (spaceId && spaceOptions.some((space) => space.id === spaceId)) return;
    const fallback = currentSpaceId
      ? spaceOptions.find((space) => space.id === currentSpaceId)?.id
      : ALL_SPACES_OPTION_ID;
    setSpaceId(fallback ?? ALL_SPACES_OPTION_ID);
  }, [currentSpaceId, spaceId, spaceOptions]);

  // Track what we've last seen from the server across renders so we
  // can distinguish "remote change the user should adopt" from "same
  // value the user last edited past". A ref dodges the stale-closure
  // trap the previous effect had — we compare against the last
  // observed room field, not against local state.
  const lastServerRef = useRef({
    name: room.name,
    topic: room.topic ?? "",
    channelType: room.channelType ?? DEFAULT_CHANNEL_TYPE,
    spaceId: currentSpaceId || ALL_SPACES_OPTION_ID,
    avatarUrl: room.avatarUrl,
  });

  // Adopt remote updates only for fields the user hasn't started
  // editing. If a field is still pristine (local === last-seen
  // server value), a remote change flows through; otherwise the
  // user's edit wins. Applies to every editable field, not just
  // name.
  useEffect(() => {
    const last = lastServerRef.current;
    const incoming = {
      name: room.name,
      topic: room.topic ?? "",
      channelType: room.channelType ?? DEFAULT_CHANNEL_TYPE,
      spaceId: currentSpaceId || ALL_SPACES_OPTION_ID,
      avatarUrl: room.avatarUrl,
    };
    setName((local) => (local === last.name ? incoming.name : local));
    setTopic((local) => (local === last.topic ? incoming.topic : local));
    setChannelType((local) => (local === last.channelType ? incoming.channelType : local));
    setSpaceId((local) => (local === last.spaceId ? incoming.spaceId : local));
    setAvatarUrl((local) => (local === last.avatarUrl ? incoming.avatarUrl : local));
    lastServerRef.current = incoming;
  }, [room.name, room.topic, room.channelType, currentSpaceId, room.avatarUrl]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedOnce, setSavedOnce] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const canShowSpaceMove = !room.isDirect && !room.isSpace && spaceOptions.length > 1;
  const canMoveSpace = useMemo(() => {
    if (!canShowSpaceMove || !client || !userId || !spaceId) return false;
    const alreadyInSelectedSpace = currentParentSpaces.some((parent) => parent.id === spaceId);
    if (
      spaceId !== ALL_SPACES_OPTION_ID &&
      !alreadyInSelectedSpace &&
      !matrixCanSendStateEvent(client, spaceId, userId, "m.space.child")
    ) {
      return false;
    }
    return currentParentSpaces.every((parent) =>
      parent.id === spaceId
        ? true
        : matrixCanSendStateEvent(client, parent.id, userId, "m.space.child"),
    );
  }, [canShowSpaceMove, client, userId, spaceId, currentParentSpaces]);

  const canEdit = useMemo(
    () => ({
      name: settings.canSetName(),
      topic: settings.canSetTopic(),
      avatar: settings.canSetAvatar(),
      channelType: settings.canSetChannelType(),
      archive: settings.canArchive(),
    }),
    [settings],
  );

  // Each field saves itself: text on leaving it, pills and the space picker
  // on change. A save carries only the fields that triggered it, with their
  // values, so an unrelated half-typed field is never written. Writes run in
  // sequence (power levels still gate each one), one save at a time; fields
  // changed meanwhile are queued with their values and replayed after.
  const savingRef = useRef(false);
  const queuedRef = useRef<Partial<EditableFields> | null>(null);
  const failedRef = useRef<Partial<EditableFields> | null>(null);
  // Values already written, so a replay or repeat blur before the room
  // reflects the change does not send it twice.
  const writtenRef = useRef<Partial<EditableFields>>({});
  const saveRef = useRef<(fields: Partial<EditableFields>) => Promise<void>>(async () => {});

  const canMoveTo = useCallback(
    (target: string) => {
      if (!canShowSpaceMove || !client || !userId || !target) return false;
      const alreadyThere = currentParentSpaces.some((parent) => parent.id === target);
      if (
        target !== ALL_SPACES_OPTION_ID &&
        !alreadyThere &&
        !matrixCanSendStateEvent(client, target, userId, "m.space.child")
      ) {
        return false;
      }
      return currentParentSpaces.every((parent) =>
        parent.id === target
          ? true
          : matrixCanSendStateEvent(client, parent.id, userId, "m.space.child"),
      );
    },
    [canShowSpaceMove, client, userId, currentParentSpaces],
  );

  const save = useCallback(
    async (fields: Partial<EditableFields>) => {
      if (savingRef.current) {
        queuedRef.current = { ...queuedRef.current, ...fields };
        return;
      }
      const written = writtenRef.current;
      const nameValue = fields.name?.trim();
      const topicValue = fields.topic?.trim();
      const writeName =
        nameValue !== undefined &&
        canEdit.name &&
        nameValue !== "" &&
        nameValue !== room.name &&
        nameValue !== written.name;
      const writeTopic =
        topicValue !== undefined &&
        canEdit.topic &&
        topicValue !== (room.topic ?? "") &&
        topicValue !== written.topic;
      const writeType =
        fields.channelType !== undefined &&
        canEdit.channelType &&
        fields.channelType !== (room.channelType ?? DEFAULT_CHANNEL_TYPE) &&
        fields.channelType !== written.channelType;
      const writeSpace =
        fields.spaceId !== undefined &&
        canMoveTo(fields.spaceId) &&
        fields.spaceId !== written.spaceId &&
        (fields.spaceId !== (currentSpaceId || ALL_SPACES_OPTION_ID) ||
          currentParentSpaces.length > 1);
      if (!writeName && !writeTopic && !writeType && !writeSpace) return;
      savingRef.current = true;
      setSaving(true);
      setError(null);
      try {
        if (writeName && nameValue !== undefined) {
          await settings.setName(nameValue);
          written.name = nameValue;
        }
        if (writeTopic && topicValue !== undefined) {
          await settings.setTopic(topicValue);
          written.topic = topicValue;
        }
        if (writeType && fields.channelType !== undefined) {
          await settings.setChannelType(fields.channelType);
          written.channelType = fields.channelType;
        }
        if (writeSpace && fields.spaceId !== undefined) {
          await moveRoomToSpace({
            roomId: room.id,
            spaceId: fields.spaceId === ALL_SPACES_OPTION_ID ? null : fields.spaceId,
          });
          written.spaceId = fields.spaceId;
        }
        failedRef.current = null;
        setSavedOnce(true);
      } catch (err) {
        failedRef.current = fields;
        setError(err instanceof Error ? err.message : "Couldn't save.");
      } finally {
        savingRef.current = false;
        setSaving(false);
        const queued = queuedRef.current;
        queuedRef.current = null;
        if (queued) void saveRef.current(queued);
      }
    },
    [
      canEdit,
      canMoveTo,
      currentSpaceId,
      currentParentSpaces.length,
      room,
      settings,
      moveRoomToSpace,
    ],
  );
  useEffect(() => {
    saveRef.current = save;
  });

  // Typed text not yet left (and so not yet written) is never called saved.
  const unsavedText =
    (name.trim() !== "" && name.trim() !== room.name && name.trim() !== writtenRef.current.name) ||
    (topic.trim() !== (room.topic ?? "") && topic.trim() !== writtenRef.current.topic);
  const saveState: SaveState = saving
    ? "saving"
    : error
      ? "error"
      : savedOnce && !unsavedText
        ? "saved"
        : "idle";

  const handleAvatarPick = useCallback(
    async (file: File) => {
      setAvatarUploading(true);
      setAvatarError(null);
      try {
        const mxc = await settings.setAvatarFromFile(file);
        setAvatarUrl(mxc);
      } catch (err) {
        setAvatarError(err instanceof Error ? err.message : "Couldn't upload the avatar.");
      } finally {
        setAvatarUploading(false);
      }
    },
    [settings],
  );

  // Avatars on Synapse ≥ 1.120 (`enable_authenticated_media: true`)
  // require an `Authorization: Bearer` header on the v1 media path —
  // which `<img src=…>` cannot supply. Fetch the bytes through the
  // SDK and feed an object URL into the `<img>` instead. The
  // download starts when `avatarUrl` becomes a non-null `mxc://`
  // URI; before then the avatar field renders the initials
  // fallback.
  const avatarMxc = avatarUrl && avatarUrl.startsWith("mxc://") ? avatarUrl : null;
  const avatarMedia = useAuthenticatedMedia(client, avatarMxc);
  const avatarHref = avatarMedia.src;
  const nameFieldId = `${useId()}-name`;
  const topicFieldId = `${useId()}-topic`;
  const channelTypeFieldId = `${useId()}-channel-type`;
  const spaceFieldId = `${useId()}-space`;
  const archived = room.archived !== null;

  const runArchive = useCallback(async () => {
    setArchiveSaving(true);
    setArchiveError(null);
    try {
      if (archived) {
        await settings.unarchive();
        pushToast({ message: "Unarchived", tone: "success" });
      } else {
        await settings.archive();
        pushToast({ message: "Archived", tone: "success" });
      }
      setArchiveDialogOpen(false);
    } catch {
      setArchiveError(
        archived ? "Could not unarchive. Try again." : "Could not archive. Try again.",
      );
    } finally {
      setArchiveSaving(false);
    }
  }, [archived, pushToast, settings]);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "var(--chat-space-4) 0" }}>
      <Section
        title="General"
        aside={
          <SaveStatus
            state={saveState}
            labels={{ saving: "Saving…", saved: "Saved", failed: error ?? "Couldn't save." }}
            action={
              <GhostButton onClick={() => void save(failedRef.current ?? {})} disabled={saving}>
                Try again
              </GhostButton>
            }
          />
        }
      >
        <AvatarField
          avatarHref={avatarHref}
          name={name}
          onPick={() => fileInputRef.current?.click()}
          disabled={!canEdit.avatar || avatarUploading}
          uploading={avatarUploading}
          error={avatarError}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleAvatarPick(file);
            // Reset the input so picking the same file twice re-fires.
            if (e.target) e.target.value = "";
          }}
        />
        {/* Text saves when it is left; blur bubbles up from each field. */}
        <div onBlur={() => void save({ name })}>
          <LabelledField id={nameFieldId} label="Name">
            <TextInput id={nameFieldId} value={name} onChange={setName} placeholder={room.name} />
            {!canEdit.name && <ReadOnlyHint />}
          </LabelledField>
        </div>
        <div onBlur={() => void save({ topic })}>
          <LabelledField id={topicFieldId} label="Topic">
            <TopicTextarea
              id={topicFieldId}
              value={topic}
              onChange={setTopic}
              disabled={!canEdit.topic}
            />
            {!canEdit.topic && <ReadOnlyHint />}
          </LabelledField>
        </div>
        <LabelledField id={channelTypeFieldId} label="Channel type">
          {canEdit.channelType ? (
            <ChannelTypePills
              value={channelType}
              onChange={(next) => {
                setChannelType(next);
                void save({ channelType: next });
              }}
            />
          ) : (
            <ReadOnlyChannelType value={channelType} />
          )}
        </LabelledField>
        {canShowSpaceMove && (
          <LabelledField id={spaceFieldId} label="Space">
            <SpaceSelect
              id={spaceFieldId}
              value={spaceId}
              onChange={(next) => {
                setSpaceId(next);
                void save({ spaceId: next });
              }}
              options={spaceOptions}
              disabled={!canMoveSpace}
            />
            {!canMoveSpace && <ReadOnlyHint />}
          </LabelledField>
        )}
      </Section>
      <IntegrationsSection room={room} />
      <Section title="Members">
        <button
          type="button"
          onClick={onOpenMembers}
          className="chat-button"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-3)",
            width: "100%",
            padding: "var(--chat-space-3) var(--chat-space-4)",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span style={{ flex: 1 }}>View members</span>
          <span
            style={{
              color: "var(--chat-fg-muted)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
            }}
          >
            {room.memberCount}
          </span>
        </button>
      </Section>
      <Section title="Danger zone">
        <button
          type="button"
          onClick={onOpenLeave}
          className="chat-button"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-3)",
            width: "100%",
            minHeight: 32,
            padding: "var(--chat-space-2) 0",
            border: "none",
            background: "transparent",
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <LogOut
            size={16}
            strokeWidth={ICON_STROKE}
            style={{ color: "var(--chat-fg-secondary)" }}
          />
          <span style={{ flex: 1 }}>{room.isDirect ? "Close conversation" : "Leave channel"}</span>
        </button>
        {canEdit.archive && (
          <ArchiveDangerControl
            roomName={room.name}
            archived={archived}
            saving={archiveSaving}
            error={archiveError}
            confirmOpen={archiveDialogOpen}
            onOpenConfirm={() => {
              setArchiveError(null);
              setArchiveDialogOpen(true);
            }}
            onCancel={() => setArchiveDialogOpen(false)}
            onConfirm={() => void runArchive()}
          />
        )}
      </Section>
    </div>
  );
}

/* ─── layout primitives ───────────────────────────────────────── */

function LabelledField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: "var(--chat-space-1)",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ReadOnlyHint() {
  return (
    <p
      style={{
        margin: "var(--chat-space-1) 0 0",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
      }}
    >
      You don’t have permission to edit this.
    </p>
  );
}

function ReadOnlyChannelType({ value }: { value: ChannelType }) {
  const label =
    value === "human-only" ? "Human-only" : value === "agent-only" ? "Agent-only" : "Mixed";
  return (
    <span
      style={{
        display: "inline-block",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-md-line)",
      }}
    >
      {label}
    </span>
  );
}

function TopicTextarea({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const underline = useUnderline();
  useAutoResizeTextarea(ref, value, { min: 40, max: 120 });
  return (
    <textarea
      ref={ref}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={underline.onFocus}
      onBlur={underline.onBlur}
      disabled={disabled}
      rows={2}
      style={{
        display: "block",
        width: "100%",
        minHeight: 40,
        maxHeight: 120,
        padding: "var(--chat-space-2) 0",
        border: "none",
        borderBottom: `1px solid ${disabled ? "var(--chat-surface-border)" : underline.borderColor}`,
        background: "transparent",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
        resize: "none",
        outline: "none",
        transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
      }}
    />
  );
}

function AvatarField({
  avatarHref,
  name,
  onPick,
  disabled,
  uploading,
  error,
}: {
  avatarHref: string | null;
  name: string;
  onPick: () => void;
  disabled: boolean;
  uploading: boolean;
  error: string | null;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        className="chat-button"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          padding: 0,
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span
          style={{
            position: "relative",
            width: "var(--chat-room-settings-avatar-size)",
            height: "var(--chat-room-settings-avatar-size)",
            borderRadius: "var(--chat-radius-md)",
            overflow: "hidden",
            background: "var(--chat-surface-panel)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {avatarHref ? (
            <img
              src={avatarHref}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <UserAvatar name={name} size={64} />
          )}
          {uploading && (
            <span
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: "var(--chat-room-settings-avatar-overlay)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--chat-fg-on-accent)",
                fontSize: "var(--chat-type-body-xs-size)",
              }}
            >
              Uploading…
            </span>
          )}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--chat-space-1)" }}>
          <Upload size={14} strokeWidth={ICON_STROKE} />
          <span>Change avatar</span>
        </span>
      </button>
      {error && (
        <p
          style={{
            margin: "var(--chat-space-1) 0 0",
            color: "var(--chat-state-danger)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function ArchiveDangerControl({
  roomName,
  archived,
  saving,
  error,
  confirmOpen,
  onOpenConfirm,
  onCancel,
  onConfirm,
}: {
  roomName: string;
  archived: boolean;
  saving: boolean;
  error: string | null;
  confirmOpen: boolean;
  onOpenConfirm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onOpenConfirm}
        disabled={saving}
        className="chat-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          border: "none",
          background: "transparent",
          color: "var(--chat-state-danger)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          cursor: saving ? "not-allowed" : "pointer",
          opacity: saving ? 0.5 : 1,
          padding: 0,
        }}
      >
        <ArchiveGlyph />
        {archived ? "Unarchive" : "Archive channel"}
      </button>
      {error && <ErrorLine>{error}</ErrorLine>}
      {confirmOpen && (
        <ArchiveConfirmDialog
          roomName={roomName}
          archived={archived}
          saving={saving}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
}

function ArchiveConfirmDialog({
  roomName,
  archived,
  saving,
  onCancel,
  onConfirm,
}: {
  roomName: string;
  archived: boolean;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={archived ? `Unarchive #${roomName}` : `Archive #${roomName}`}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--chat-scrim-dialog)",
        zIndex: 900,
      }}
    >
      <div
        style={{
          width: 360,
          padding: "var(--chat-space-5)",
          borderRadius: "var(--chat-radius-xl)",
          background: "var(--chat-surface-dialog)",
          border: "1px solid var(--chat-surface-border)",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          boxShadow: "var(--chat-shadow-dialog)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          {archived
            ? `Unarchive #${roomName}? Members will be able to post again.`
            : `Archive #${roomName}? Members will no longer be able to post. History stays visible.`}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--chat-space-2)",
            marginTop: "var(--chat-space-5)",
          }}
        >
          <GhostButton onClick={onCancel} disabled={saving}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={onConfirm} disabled={saving}>
            {saving ? "Saving…" : archived ? "Unarchive" : "Archive"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ArchiveGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
      <path d="M1 3h22v5H1z" />
      <path d="m8 13 4 4 4-4" />
      <path d="M12 17V9" />
    </svg>
  );
}
